<template>
  <b-row class="margin-0">
    <b-col cols="12" class="padding-0 height--max-content">
      <ListWrapper
        ref="listWrapperReference"
        :url="listUrl"
        :extraParams="{ order_by: 'executive_role_id:asc-id:asc' }"
        :title="$lang.centerExecutePersons"
        :isUseInSidebar="true"
        :addButtonText="$lang.newPerson"
        menuBarClass="padding-x-16"
        searchTxtClass="width-percent-100"
        wrapperClass="background-surface border-radius-8 border--solid border--complementary-50 padding-y-16"
        wrapperListContentStyle="height: calc(100% - 177px) !important;"
        titleClass="padding-x-16"
        :hasAddButton="isEditState"
        :extraButtons="extraButtons"
        @getPermissions="getPermissions"
        @getOtherData="updateOtherData"
        @handleAddButton="handleOpenAddPin"
      >
        <template #out-area>
          <Alert
            class="margin-bottom-24"
            :text="$lang.centerExecutePersonAlert"
            variant="light-warning"
          />
        </template>
        <template slot-scope="{ items, itemsLoading }">
          <LoadingWrapper :loading="itemsLoading">
            <template #loading>
              <ListLoading :count="5" />
            </template>
            <template #default>
              <b-row class="margin-0 padding-x-16">
                <b-col
                  cols="12"
                  v-for="(item, index) in generateItems(items)"
                  :key="index"
                  class="border--solid border--complementary-50 border-radius-8 margin-bottom-8 padding-0"
                  :class="{
                    'margin-bottom-100':
                      index === generateItems(items).length - 1,
                  }"
                >
                  <PersonItem :item="item" />
                </b-col>
              </b-row>
            </template>
          </LoadingWrapper>
        </template>
        <template #empty-box>
          <b-row class="margin-0 padding-16">
            <b-col
              v-if="isEditState"
              cols="12"
              class="border--dashed border--complementary-100 border-radius-8 padding-y-32 flex-center flex-direction-column"
            >
              <div
                class="font-size-14 color-complementary-300 margin-bottom-16"
              >
                {{ $lang.clickForAddNewPerson }}
              </div>
              <Button
                rightIcon="plus"
                variant="complementary"
                :text="$lang.newPerson"
                @click="handleOpenAddPin"
              />
            </b-col>
            <b-col
              cols="12"
              class="flex-center flex-direction-column margin-top-16"
            >
              <b-img-lazy
                class="empty-box__image width-181 margin-bottom-21"
                :src="generateEmptyImage"
              />
              <div class="empty-box__text color-complementary-300 font-size-14">
                {{ $lang.list_empty_text }}
              </div>
            </b-col>
          </b-row>
        </template>
      </ListWrapper>
      <AddPersonPin
        :visible="isAddPersonPin"
        @handleClose="handleCloseAddPin"
      />
      <Modal
        :show="isRemoveCenterExecuteReportsModal"
        :title="$lang.removeAllAcReports"
        :closeOnBackdrop="false"
        :hasClose="true"
        :hasFooter="true"
        size="sm"
        :okButton="true"
        okButtonVariant="filled-error"
        :okButtonText="$lang.delete"
        :ok="handleRemoveCenterExecuteReports"
        @close="handleCloseRemoveCenterExecuteReportsModal"
      >
        <template #body>
          <div class="margin-bottom-24 font-size-14 color-complementary-300">
            {{ $lang.removeAllAcReportsConfirm }}
          </div>
        </template>
      </Modal>
      <Modal
        :show="isRebuildCenterExecuteReportsModal"
        :title="$lang.rebuildReportsForAllCandidates"
        :closeOnBackdrop="false"
        :hasClose="true"
        :hasFooter="true"
        size="sm"
        :okButton="true"
        okButtonVariant="filled-primary"
        :okButtonText="$lang.buildReport"
        :ok="handleRebuildCenterExecuteReports"
        @close="handleCloseRebuildCenterExecuteReportsModal"
      >
        <template #body>
          <div class="margin-bottom-24 font-size-14 color-complementary-300">
            {{ $lang.rebuildReportConfirm }}
          </div>
        </template>
      </Modal>
      <ReportSettingPin
        :visible="isReportSettingPin"
        @handleClose="handleCloseReportSettingPin"
      />
    </b-col>
  </b-row>
</template>

<script>
import {
  ListWrapper,
  Alert,
  LoadingWrapper,
  Modal,
} from "@/components/re-design";
import { Button } from "@/components/re-design/forms";
import ListLoading from "./ListLoading.vue";
import PersonItem from "./PersonItem.vue";
import AddPersonPin from "./AddPersonPin.vue";
import ReportSettingPin from "./reportSetting/ReportSettingPin.vue";
import { pathOr } from "ramda";
import { computed, inject, provide, ref } from "vue";
import {
  generateApiUrl,
  generateError,
  generateLang,
  toastAlert,
  generateConfig,
} from "@/shared/utils";
import {
  rebuildCenterExecuteReports,
  removeCenterExecuteReport,
} from "@/modules/kanoon/services";
export default {
  components: {
    Alert,
    ListWrapper,
    LoadingWrapper,
    Modal,
    Button,
    ListLoading,
    PersonItem,
    AddPersonPin,
    ReportSettingPin,
  },
  setup() {
    const centerExecuteData = inject("CENTER_EXECUTE_DATA");

    const centerExecuteId = pathOr(null, ["id"], centerExecuteData.value);
    const listUrl = generateApiUrl(
      ["kanoon", "centerExecutePerson"],
      [centerExecuteId]
    );

    const generateEmptyImage = generateConfig([
      "images",
      "shared",
      "redesign",
      "empty",
      "list",
    ]);

    // handle permissions
    const permissions = ref([]);
    const getPermissions = (data) => (permissions.value = data);
    const otherData = ref(null);
    const updateOtherData = (data) => (otherData.value = data);

    const isEditState = computed(
      () => pathOr(null, ["center_execute_status", "id"], otherData.value) === 1
    );

    // generate list
    const generateItems = (items) => {
      return items.map((item) => {
        const parvandeh = pathOr(null, ["parvandeh"], item);
        const parvandehCode = pathOr(null, ["code"], parvandeh);
        const parvandehNumber = pathOr("", ["parvandeh_number"], parvandeh);
        const personNationalCode = pathOr(
          "",
          ["person", "national_code"],
          parvandeh
        );
        const personName = pathOr("", ["person", "name"], parvandeh);
        const personAvatar = pathOr("", ["person", "image_address"], parvandeh);
        const termCaption = pathOr("", ["term_caption"], parvandeh);

        const secondPerson = pathOr(null, ["second_assessor_parvandeh"], item);
        const secondPersonParvandehNumber = pathOr(
          "",
          ["parvandeh_number"],
          secondPerson
        );
        const secondPersonParvandehCode = pathOr(null, ["code"], secondPerson);
        const secondPersonNationalCode = pathOr(
          "",
          ["person", "national_code"],
          secondPerson
        );
        const secondPersonName = pathOr("", ["person", "name"], secondPerson);
        const secondPersonAvatar = pathOr(
          "",
          ["person", "image_address"],
          secondPerson
        );
        const secondPersonTermCaption = pathOr(
          "",
          ["term_caption"],
          secondPerson
        );

        const executiveRole = pathOr(null, ["executive_role"], item);

        const candidateSearchWithNationalCode = pathOr(
          false,
          ["candidateSearchWithNationalCode"],
          centerExecuteData.value
        );
        const executivePersonSearchWithNationalCode = pathOr(
          false,
          ["executivePersonSearchWithNationalCode"],
          centerExecuteData.value
        );
        const executivePersonTermFilter = pathOr(
          [],
          ["executivePersonTermFilter"],
          centerExecuteData.value
        );

        return {
          id: pathOr(null, ["id"], item),
          isAbsentId: pathOr(0, ["is_absent", "id"], item),
          isAbsentCaption: pathOr("", ["is_absent", "caption"], item),
          isCreateReport: pathOr(false, ["is_create_report"], item),
          isPlanningPerson: pathOr(0, ["is_planing_person"], item),
          centerExecuteState: pathOr(
            null,
            ["center_execute_status", "id"],
            otherData.value
          ),
          executiveRoleId: pathOr(null, ["id"], executiveRole),
          executiveRoleCaption: pathOr("", ["caption"], executiveRole),
          candidateSearchWithNationalCode,
          executivePersonSearchWithNationalCode,
          executivePersonTermFilter,
          parvandehCode,
          parvandehNumber,
          personNationalCode,
          personName,
          personAvatar,
          personCode: pathOr(null, ["person", "code"], parvandeh),
          personCenterExecuteCode: pathOr(
            "",
            ["person_center_execute_code"],
            item
          ),
          secondPerson,
          secondPersonCode: pathOr(null, ["person", "code"], secondPerson),
          secondPersonName,
          secondPersonAvatar,
          secondPersonParvandehNumber,
          secondPersonParvandehCode,
          secondPersonNationalCode,
          oneDropdown: parvandeh
            ? {
                id: parvandehCode,
                caption: `${personNationalCode} - ${personName} - ${termCaption}`,
              }
            : null,
          twoDropdown: secondPerson
            ? {
                id: pathOr("", ["code"], secondPerson),
                caption: `${secondPersonNationalCode} - ${secondPersonName} - ${secondPersonTermCaption}`,
              }
            : null,
          executiveRole: {
            id: pathOr("", ["id"], executiveRole),
            caption: pathOr("", ["caption"], executiveRole),
          },
        };
      });
    };

    // handle add
    const isAddPersonPin = ref(false);
    const handleOpenAddPin = () => (isAddPersonPin.value = true);
    const handleCloseAddPin = () => (isAddPersonPin.value = false);

    // handle reload list
    const listWrapperReference = ref(null);
    const handleReloadList = () => listWrapperReference.value.reload();

    // handle remove center execute reports
    const isRemoveCenterExecuteReportsModal = ref(false);
    const handleOpenRemoveCenterExecuteReportsModal = () => {
      isRemoveCenterExecuteReportsModal.value = true;
    };
    const handleCloseRemoveCenterExecuteReportsModal = () => {
      isRemoveCenterExecuteReportsModal.value = false;
    };
    const handleRemoveCenterExecuteReports = async () => {
      try {
        const centerExecuteCode = pathOr(
          null,
          ["code"],
          centerExecuteData.value
        );
        const response = await removeCenterExecuteReport(centerExecuteCode);
        const { message } = response;
        toastAlert(message);
        handleCloseRemoveCenterExecuteReportsModal();
        handleReloadList();
      } catch (error) {
        generateError(error);
      }
    };

    // handle rebuild center execute all reports
    const isRebuildCenterExecuteReportsModal = ref(false);
    const handleOpenRebuildCenterExecuteReportsModal = () => {
      isRebuildCenterExecuteReportsModal.value = true;
    };
    const handleCloseRebuildCenterExecuteReportsModal = () => {
      isRebuildCenterExecuteReportsModal.value = false;
    };
    const handleRebuildCenterExecuteReports = async () => {
      try {
        const centerExecuteCode = pathOr(
          null,
          ["code"],
          centerExecuteData.value
        );
        const response = await rebuildCenterExecuteReports(centerExecuteCode);
        const { message } = response;
        toastAlert(message);
        handleCloseRebuildCenterExecuteReportsModal();
      } catch (error) {
        generateError(error);
      }
    };

    // handle report setting
    const isReportSettingPin = ref(false);
    const handleOpenReportSettingPin = () => (isReportSettingPin.value = true);
    const handleCloseReportSettingPin = () =>
      (isReportSettingPin.value = false);

    // handle extra buttons
    const extraButtons = computed(() => {
      return [
        {
          caption: generateLang(["rebuildReportsForAllCandidates"]),
          show: true,
          permissions: ["admin", "edit", "view"],
          click: handleOpenRebuildCenterExecuteReportsModal,
        },
        {
          caption: generateLang(["removeAllAcReports"]),
          show: true,
          permissions: ["admin", "edit", "view"],
          click: handleOpenRemoveCenterExecuteReportsModal,
        },
        {
          caption: generateLang(["reportSetting"]),
          show: true,
          permissions: ["admin", "edit", "view"],
          click: handleOpenReportSettingPin,
        },
      ];
    });

    // provide data
    provide(
      "PERMISSIONS",
      computed(() => permissions.value)
    );
    provide("handleReloadList", handleReloadList);

    // return data to be used in template
    return {
      listUrl,
      getPermissions,
      isAddPersonPin,
      handleOpenAddPin,
      handleCloseAddPin,
      generateItems,
      listWrapperReference,
      extraButtons,
      isRemoveCenterExecuteReportsModal,
      handleCloseRemoveCenterExecuteReportsModal,
      handleRemoveCenterExecuteReports,
      isRebuildCenterExecuteReportsModal,
      handleCloseRebuildCenterExecuteReportsModal,
      handleRebuildCenterExecuteReports,
      isReportSettingPin,
      handleCloseReportSettingPin,
      otherData,
      updateOtherData,
      isEditState,
      generateEmptyImage,
    };
  },
};
</script>
